import { isDev, isProductionHost } from 'lib/env'
import { VERTICAL } from 'lib/utils/store/vehicle'

// These should match the config.css breakpoints
export const WINDOW_BREAKPOINTS = Object.freeze({
    containerZero: 0,
    containerXS: 340,
    containerS: 480,
    containerM: 777,
    container: 980,
    containerL: 1170,
})

export const DEV_BACKEND = isDev() ? (
    process.env.DEV_BACKEND ? process.env.DEV_BACKEND : 'http://localhost'
) : null

export const dataURI = isDev() ? `${DEV_BACKEND}/graphql` : '/graphql'
export const credentialsSetting = isDev() ? 'include' : 'same-origin'

const authURI = isDev() ? `${DEV_BACKEND}/sso` : '/sso'
export const loginURI = authURI + '/login'
export const logoutURI = authURI + '/logout'
export const refreshURI = authURI + '/refresh'

export const nettiautoURI = isProductionHost() ? 'https://www.nettiauto.com' : 'https://nettiauto.almaajo.dev'
export const rekkariURI = 'https://rekkari.fi/'

export const autocompleteFormInput = isProductionHost() ? 'new-password' : 'on'

export const DIST_PATH = window.location.protocol + '//' + window.location.hostname

export const FINLAND_ID = 73

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

export const TURNSTILE_SITE_KEY = isProductionHost() ? '0x4AAAAAAAOiUQj3YxVHlz1-' : '0x4AAAAAAAOijg3sPokdmh_F'
// export const TURNSTILE_SITE_KEY = '1x00000000000000000000AA'

export const LOGIN_STATUS = Object.freeze({
    SUCCESS: 'success',
    FAILED: 'failed',
})

export const LOGOUT_STATUS = LOGIN_STATUS

export const OFFER_STATUS = Object.freeze({
    PENDING: 'PENDING',
    DECLINED: 'DECLINED',
    ACCEPTED: 'ACCEPTED',
})

export const PHONE_VERIFICATION_STATUS = Object.freeze({
    CONFIRMED: 'CONFIRMED',
    NOTCONFIRMED: 'NOTCONFIRMED'
})

export const NETTIX_VERTICAL_DOMAINS = Object.freeze([
    'baana.nettiauto.com',
    'nettiauto.com',
    'nettimoto.com',
    'nettivene.com',
    'nettikone.com',
    'nettikaravaani.com',
    'nettivaraosa.com',
    'nettimokki.com',
    'nettivuokraus.com',
    'rekkari.fi/',
    'autokatos.com',
    'beta.nettiauto.com',
    'viestit.almaajo.fi'
])

export const NETTIX_TEST_DOMAINS = Object.freeze([
    'nettiauto.almaajo.dev',
    'nettimoto.almaajo.dev',
    'nettivene.almaajo.dev',
    'nettikone.almaajo.dev',
    'nettivaraosa.almaajo.dev',
    'nettikaravaani.almaajo.dev',
    'viestit.almaajo.dev',
    'beta-nettiauto.almaajo.dev',
    'rekkari.almaajo.dev'
])

export const NETTIX_DOMAINS = Object.freeze([
    ...NETTIX_VERTICAL_DOMAINS,
    ...NETTIX_TEST_DOMAINS,
    'nettix.fi',
    'almaajo.fi',
])

export const ALLOW_REDIRECT_URLS = Object.freeze(!isProductionHost() ? {
    domains: [
        ...NETTIX_VERTICAL_DOMAINS,
        'viestit.almaajo.fi',
        ...NETTIX_TEST_DOMAINS,
    ],
    expr: [
        '^mv-[a-z,0-9-]*\\.test\\.nettix-aws\\.com$',
        '^nw-[a-z,0-9-]*\\.[a-z-]*\\.test\\.nettix-aws\\.com$'
    ],
} : {
    domains: [
        ...NETTIX_VERTICAL_DOMAINS,
        'viestit.almaajo.fi',
    ],
    expr: [],
})

export const VERTICAL_BY_NETTIX_DOMAIN = Object.freeze({
    'nettiauto.com': VERTICAL.CAR,
    'nettimoto.com': VERTICAL.BIKE,
    'nettivene.com': VERTICAL.BOAT,
    'nettikone.com': VERTICAL.MACHINE,
    'nettikaravaani.com': VERTICAL.CARAVAN,
    'nettivaraosa.com': VERTICAL.PART,
    'nettimokki.com': VERTICAL.CABIN,
    'nettivuokraus.com': VERTICAL.RENT,
    'rekkari.fi/': VERTICAL.REKKARI,
    'autokatos.com': VERTICAL.BAANA,
    'baana.nettiauto.com': VERTICAL.BAANA
})

export const VERTICAL_BY_NETTIX_TEST_DOMAINS = Object.freeze({
    'nettiauto.almaajo.dev': VERTICAL.CAR,
    'nettimoto.almaajo.dev': VERTICAL.BIKE,
    'nettivene.almaajo.dev': VERTICAL.BOAT,
    'nettikone.almaajo.dev': VERTICAL.MACHINE,
    'nettivaraosa.almaajo.dev': VERTICAL.PART,
    'nettikaravaani.almaajo.dev': VERTICAL.CARAVAN,
    'viestit.almaajo.dev': VERTICAL.INBOX,
    'beta-nettiauto.almaajo.dev': VERTICAL.CAR,
    'rekkari.almaajo.dev': VERTICAL.REKKARI
})

export const ALLOWED_IMAGE_TYPES = {
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
}

export const MAX_IMAGE_UPLOAD_SIZE = 15000000

export const ALLOWED_ATTACHMENT_TYPES = {
    ...ALLOWED_IMAGE_TYPES,
    'image/bmp': ['.bmp'],
    'image/gif': ['.gif'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'text/plain': ['.txt'],
    'application/pdf': ['.pdf'],
    'text/csv': ['.csv'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.oasis.opendocument.presentation': ['.odp'],
    'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
}

export const MAX_ATTACHMENT_UPLOAD_SIZE = MAX_IMAGE_UPLOAD_SIZE

export const MIME_TYPE_TO_EXTENSION = {
    'image/jpeg': ['jpeg', 'jpg'],
    'image/png': 'png',
    'image/bmp': 'bmp',
    'image/gif': 'gif',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'text/plain': 'txt',
    'application/pdf': 'pdf',
    'text/csv': 'csv',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.oasis.opendocument.presentation': 'odp',
    'application/vnd.oasis.opendocument.spreadsheet': 'ods',
    'application/vnd.oasis.opendocument.text': 'odt',
}

export const MAX_PRICE_OF_VERIFIED_PAYMENT = 40000

// TODO: This is the date when the new service price is release
export const NEW_SERVICE_PRICE_RELEASE_DATE = '2024-09-02T00:00:00+00:00'
export const NEW_FIXED_SERVICE_PRICE_RELEASE_DATE = '2025-03-26T00:00:00+00:00'
